import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import _ from 'lodash'
import { injectIntl, FormattedMessage } from 'react-intl'
import { ColumnColorChart } from 'gnos-charts'
import Tooltip, { ALIGN } from 'gnos-tooltip'
import { DataTable, DATA_TABLE_CONSTANTS as CONSTANTS } from 'gnos-tables'

import config from '../../../../../../../../../config'
import { createDataTableId } from '../../../../../../helpers'

import { LOCALE_PREFIX } from './constants'
import { ENTITY_CONTEXT_MENU_ITEM } from '../../../../../../../components/Workspace/components/EntityContextMenu/constants'

import FormattedNumber from '../../../../../../../../../services/translations/components/FormattedNumber'
import NoData from '../../../../../../../components/Workspace/components/NoData'

import EntityContextMenu from '../../../../../../../components/Workspace/components/EntityContextMenu'
import Flag from '../../../../../../../../../components/TableCell/components/Flag'
import Money from '../../../../../../../../../components/TableCell/components/Money'

import hocBlock from '../../../../../../../components/Workspace/components/HocBlock'

import './style.scss'

@hocBlock(config.modules.portfolio.key, { levelReload: true })
@injectIntl
export default class PaymentIndex extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        data: PropTypes.object,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object.isRequired,
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        locale: PropTypes.string.isRequired,
        token: PropTypes.string.isRequired,
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.block !== nextProps.block) return true
        if (!_.isEqual(this.props.data !== nextProps.data)) return true
        if (this.props.id !== nextProps.id) return true
        if (this.props.level !== nextProps.level) return true
        if (this.props.locale !== nextProps.locale) return true
        if (this.props.token !== nextProps.token) return true

        return false
    }

    getColumnDefinitions = () => {
        const { level } = this.props

        const commonColumns = [
            {
                Header: '',
                accessor: 'country',
                Cell: ({ value }) => <Flag country={value} />,
                width: 32,
                resizable: false,
            },
            {
                Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.name`} />,
                accessor: 'name',
                width: 300,
            },
        ]

        let levelColumns = []
        switch (level) {
            case 30: {
                levelColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.indexLight`} />,
                        accessor: 'p12Light',
                        suffix: (
                            <Tooltip
                                key={'suffix'}
                                id={'indexLight_data_table_info'}
                                content={
                                    <FormattedMessage
                                        id={`${LOCALE_PREFIX}.table.header.indexLight.info`}
                                        values={{
                                            br: <br />,
                                            ul: (chunks) => (
                                                <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>{chunks}</ul>
                                            ),
                                            li: (chunks) => <li>{chunks}</li>,
                                        }}
                                    />
                                }
                                align={ALIGN.CENTER}
                            >
                                <div className={'payment-index-header'} />
                            </Tooltip>
                        ),
                        showArrow: {
                            direction: CONSTANTS.DATA_TABLE_ARROW.DOWN,
                            text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.indexLight.sort`} />,
                        },
                    },
                ]
                break
            }
            case 50: {
                levelColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.index`} />,
                        accessor: 'p12Nbr',
                        showArrow: {
                            direction: CONSTANTS.DATA_TABLE_ARROW.DOWN,
                            text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.index.sort`} />,
                        },
                    },
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.supCnt12`} />,
                        accessor: 'supCnt12',
                        Cell: (value) => {
                            if (value.original.supCnt12) {
                                return <span>{value.original.supCnt12}</span>
                            }

                            return <span>-</span>
                        },
                    },
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.invoiceCnt12`} />,
                        accessor: 'invoiceCnt12',
                        Cell: (value) => {
                            if (value.original.invoiceCnt12) {
                                return <span>{value.original.invoiceCnt12}</span>
                            }

                            return <span>-</span>
                        },
                    },
                    {
                        id: 'invoiceAmount',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.invoiceAmount`} />,
                        accessor: ({ invoiceAmount, invoiceCrcy: currency }) => ({ invoiceAmount, currency }),
                        Cell: ({ value: { currency, invoiceAmount } }) => {
                            return (
                                <Money
                                    decimalPlaces={2}
                                    value={{
                                        amount: invoiceAmount,
                                        currency,
                                    }}
                                    emptyValue={'-'}
                                />
                            )
                        },
                    },
                ]
                break
            }
            default:
                levelColumns = []
        }

        return [...commonColumns, ...levelColumns]
    }

    renderSummary = (dataProvider, blockData) => {
        const average = blockData.avgPaymentIndex ? (
            <div className={'average'}>
                <div className={'days'} style={{ color: blockData.avgPaymentIndexColor }}>
                    {blockData.avgPaymentIndex}
                </div>
                <div className={'texts'}>
                    <div>
                        <FormattedMessage id={`${LOCALE_PREFIX}.average.line1`} values={{ days: blockData.daysCnt }} />
                    </div>
                    <div>
                        <FormattedMessage id={`${LOCALE_PREFIX}.average.line2`} />
                    </div>
                </div>
            </div>
        ) : null

        const dataTable =
            blockData.avgSup || blockData.invoiceCnt || blockData.totalCzkAmount || blockData.totalEurAmount ? (
                <div className={'dataTable'}>
                    <div className={'titles'}>
                        <div>{blockData.avgSup && <FormattedMessage id={`${LOCALE_PREFIX}.supplierCount`} />}</div>
                        <div>{blockData.invoiceCnt && <FormattedMessage id={`${LOCALE_PREFIX}.invoiceCount`} />}</div>
                        <div>
                            {blockData.totalCzkAmount && <FormattedMessage id={`${LOCALE_PREFIX}.invoiceVolumeCZK`} />}
                        </div>
                        <div>
                            {blockData.totalEurAmount && <FormattedMessage id={`${LOCALE_PREFIX}.invoiceVolumeEUR`} />}
                        </div>
                    </div>
                    <div className={'values'}>
                        <div>{blockData.avgSup && <FormattedNumber value={blockData.avgSup} />}</div>
                        <div>{blockData.invoiceCnt && <FormattedNumber value={blockData.invoiceCnt} />}</div>
                        <div>
                            {blockData.totalCzkAmount && (
                                <Money
                                    value={{
                                        amount: blockData.totalCzkAmount,
                                        currency: blockData.totalCzkAmountCurrency,
                                    }}
                                />
                            )}
                        </div>
                        <div>
                            {blockData.totalEurAmount && (
                                <Money
                                    value={{
                                        amount: blockData.totalEurAmount,
                                        currency: blockData.totalEurAmountCurrency,
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            ) : null

        const summary =
            average || dataTable ? (
                <div className={'summary'}>
                    {average}
                    {dataTable}
                </div>
            ) : null

        return summary
    }

    render() {
        const { data, block, id, level, locale, token, intl } = this.props

        const blockData = _.get(data, 'data', null)
        const dataProvider = _.get(blockData, 'chartData', null)

        if (!blockData) return null

        if (!dataProvider) {
            return <NoData text={<FormattedMessage id={`${LOCALE_PREFIX}.noData`} />} />
        }

        const maxValue = _.get(_.maxBy(dataProvider, 'value'), 'value', 0)
        const numberOfDigits = _.toString(maxValue).length

        return (
            <div className={cx('block-payment-index')}>
                {this.renderSummary(dataProvider, blockData)}
                <ColumnColorChart
                    style={{
                        width: '100%',
                        height: '400px',
                    }}
                    dataProvider={dataProvider}
                    greyColumnLabels={['NA', 'N/A', '-']}
                    spaceAxisY={numberOfDigits * 8 + 11}
                />
                <DataTable
                    id={createDataTableId(config.modules.portfolio.key, id, block)}
                    uriSettings={_.merge({}, config.api.portfolio[block].table, {
                        qs: { groupId: id, level, token, lang: config.languages[locale].requestKey },
                    })}
                    urlSettingsReloadPaths={[{ path: 'qs.level', reset: true }, { path: 'qs.lang' }]}
                    columnsDefinition={this.getColumnDefinitions()}
                    hide={level < 30}
                    contextMenu={
                        <EntityContextMenu
                            id={id}
                            items={[
                                ENTITY_CONTEXT_MENU_ITEM.ADD_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REMOVE_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REPORT,
                                ENTITY_CONTEXT_MENU_ITEM.LINKAGES,
                                ENTITY_CONTEXT_MENU_ITEM.DISTRAINTS,
                            ]}
                        />
                    }
                    texts={{
                        previousText: intl.formatMessage({ id: 'global.previous' }),
                        nextText: intl.formatMessage({ id: 'global.next' }),
                        loadingText: intl.formatMessage({ id: 'global.loading' }),
                        noDataText: intl.formatMessage({ id: 'global.noData' }),
                        pageText: intl.formatMessage({ id: 'global.page' }),
                        ofText: intl.formatMessage({ id: 'global.of' }),
                        rowsText: intl.formatMessage({ id: 'global.rows' }),
                    }}
                    itemIdentifier={'entId'}
                    showHeaderTooltips
                    showPageSizeOptions
                />
            </div>
        )
    }
}
